import { t } from 'i18next'

import Tooltip from '~/components/Tooltip'

type RoasRaw = Partial<
  Record<'roas' | 'overall_roas' | 'roas_overall', unknown>
>

type RoasMetricCellProps = {
  metric: 'roas' | 'overall_roas'
  value?: React.ReactNode
  raw?: RoasRaw | null
}

/**
 * Renders a ROAS cell. The API returns a null ROAS when it is undefined (zero
 * cost with return), so "-" with a tooltip is shown instead of a misleading 0.
 * The rule itself lives in the API; this only presents it.
 */
export function RoasMetricCell({
  metric,
  value,
  raw
}: Readonly<RoasMetricCellProps>) {
  // overall ROAS has two aliases (the formatter reads roas_overall ?? overall_roas),
  // so it is undefined only when one is null and neither carries a value
  const overallAliases = [raw?.roas_overall, raw?.overall_roas]
  const isUndefinedRoas =
    metric === 'overall_roas'
      ? overallAliases.includes(null) && overallAliases.every(v => v == null)
      : raw?.roas === null

  if (isUndefinedRoas) {
    const explanation = t('rm:metrics.roas.noCostTooltip')

    return (
      <Tooltip
        noMargin
        icon={<span>-</span>}
        text={explanation}
        ariaLabel={explanation}
      />
    )
  }

  return <>{value ?? '-'}</>
}
